import React, { useEffect, useState } from 'react';
import { User, UserGroup, Venue } from '@/types';
// Fixed: Added missing ShieldCheck import from lucide-react
import {
  Plus,
  Trash2,
  Edit2,
  PenTool,
  X,
  MapPin,
  Loader2,
  ShieldAlert,
  Sparkles,
  ShieldCheck,
  Search
} from 'lucide-react';
import { showAlertModal } from '@shared/components/common/AlertModal';
import { createId } from '@/lib/ids';
import { api } from '@/services/api';
import { Pagination } from '@shared/components/common/Pagination';
import { normalizeErrorMessage } from '@/lib/errorUtils';

interface StaffTabProps {
  userGroups: UserGroup[];
  venues: Venue[];
  currentUserGroup: UserGroup | undefined;
  onAddUser: (user: User) => void;
  onUpdateUser: (user: User) => void;
  onDeleteUser: (userId: string) => Promise<void>;
  canEdit: boolean;
}

const deriveRoleFromGroup = (userGroupId: string | undefined, groups: UserGroup[]) => {
  if (!userGroupId) return 'staff';
  if (userGroupId === 'ug_tech') return 'technician';
  const group = groups.find((g) => g.id === userGroupId);
  if (group?.type === 'admin' || group?.type === 'global_admin') return 'admin';
  return 'staff';
};

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const SignatureCanvas = ({
  onSave,
  onCancel,
  initialDataUrl
}: {
  onSave: (dataUrl: string) => void;
  onCancel: () => void;
  initialDataUrl?: string;
}) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = React.useState(false);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#000';
      }
    }
  }, []);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !initialDataUrl) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
      const drawWidth = img.width * scale;
      const drawHeight = img.height * scale;
      const dx = (canvas.width - drawWidth) / 2;
      const dy = (canvas.height - drawHeight) / 2;
      ctx.drawImage(img, dx, dy, drawWidth, drawHeight);
    };
    img.src = initialDataUrl;
  }, [initialDataUrl]);

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x =
      'touches' in e
        ? e.touches[0].clientX - rect.left
        : (e as React.MouseEvent).nativeEvent.offsetX;
    const y =
      'touches' in e
        ? e.touches[0].clientY - rect.top
        : (e as React.MouseEvent).nativeEvent.offsetY;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x =
      'touches' in e
        ? e.touches[0].clientX - rect.left
        : (e as React.MouseEvent).nativeEvent.offsetX;
    const y =
      'touches' in e
        ? e.touches[0].clientY - rect.top
        : (e as React.MouseEvent).nativeEvent.offsetY;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="bg-white border-2 border-dashed border-slate-300 rounded-xl mb-4 relative h-56 overflow-hidden shadow-inner">
        <canvas
          ref={canvasRef}
          className="w-full h-full cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={() => setIsDrawing(false)}
          onMouseLeave={() => setIsDrawing(false)}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={() => setIsDrawing(false)}
        />
        <div className="absolute top-2 right-2">
          <button
            onClick={clearCanvas}
            className="bg-white/80 p-2 rounded-full hover:bg-white shadow-sm text-slate-500"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded font-bold"
        >
          Cancel
        </button>
        <button
          onClick={() => canvasRef.current && onSave(canvasRef.current.toDataURL())}
          className="bg-slate-900 text-white px-6 py-2 rounded-xl font-black hover:bg-slate-800 transition-all flex items-center gap-2 uppercase tracking-wide text-xs"
        >
          Save Signature
        </button>
      </div>
    </div>
  );
};

const UserModal = ({
  user,
  groups,
  venues,
  currentUserGroup,
  onSave,
  onClose
}: {
  user: Partial<User>;
  groups: UserGroup[];
  venues: Venue[];
  currentUserGroup: UserGroup;
  onSave: (u: User) => Promise<void> | void;
  onClose: () => void;
}) => {
  const [formData, setFormData] = useState<Partial<User>>({
    name: user.name || '',
    email: user.email || '',
    role: deriveRoleFromGroup(user.userGroupId, groups),
    userGroupId: user.userGroupId || '',
    assignedVenueIds: user.assignedVenueIds || [],
    id: user.id || undefined,
    signature: user.signature || undefined
  });
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);

  const availableGroups = groups.filter((g) => {
    if (currentUserGroup.type !== 'global_admin' && g.type === 'global_admin') return false;
    return true;
  });

  const toggleVenueAssignment = (venueId: string) => {
    const current = formData.assignedVenueIds || [];
    if (current.includes(venueId)) {
      setFormData({ ...formData, assignedVenueIds: current.filter((id) => id !== venueId) });
    } else {
      setFormData({ ...formData, assignedVenueIds: [...current, venueId] });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      {isSignatureModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-scale-up overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center bg-slate-50">
              <h3 className="font-black text-slate-800 flex items-center gap-2 uppercase tracking-tight text-sm">
                <PenTool className="w-4 h-4 text-teal-600" /> Signature Capture
              </h3>
              <button onClick={() => setIsSignatureModalOpen(false)}>
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="p-6">
              <SignatureCanvas
                onSave={(url) => {
                  setFormData((prev) => ({ ...prev, signature: url }));
                  setIsSignatureModalOpen(false);
                }}
                onCancel={() => setIsSignatureModalOpen(false)}
                initialDataUrl={formData.signature}
              />
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl p-6 max-md w-full animate-fade-in max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold text-slate-800">
            {formData.id ? 'Edit Staff Member' : 'Add New Staff Member'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
              Full Name
            </label>
            <input
              className="w-full border rounded-lg p-2.5 bg-white text-slate-900 outline-none focus:ring-2 focus:ring-teal-500 transition-all"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g. John Smith"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
              Email Address
            </label>
            <input
              type="email"
              className="w-full border rounded-lg p-2.5 bg-white text-slate-900 outline-none focus:ring-2 focus:ring-teal-500 transition-all"
              value={formData.email || ''}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="name@k2occuhealth.com"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
              Permission Group
            </label>
            <select
              className="w-full border rounded-lg p-2.5 bg-white text-slate-900 outline-none focus:ring-2 focus:ring-teal-500 transition-all"
              value={formData.userGroupId || ''}
              onChange={(e) => {
                const groupId = e.target.value;
                setFormData({
                  ...formData,
                  userGroupId: groupId,
                  role: deriveRoleFromGroup(groupId, groups)
                });
              }}
            >
              <option value="" disabled>
                Select access level...
              </option>
              {availableGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>

          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
            <label className="block text-xs font-bold text-slate-500 uppercase mb-3 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-teal-600" /> Site Authorization
            </label>
            <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
              {venues.map((v) => (
                <label
                  key={v.id}
                  className="flex items-center gap-3 cursor-pointer p-2 hover:bg-white rounded-lg transition-colors group border border-transparent hover:border-slate-200"
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-teal-600 rounded"
                    checked={formData.assignedVenueIds?.includes(v.id)}
                    onChange={() => toggleVenueAssignment(v.id)}
                  />
                  <span className="text-sm text-slate-700 group-hover:text-teal-700">{v.name}</span>
                </label>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-3 italic leading-relaxed">
              If no sites are selected, this user will have access to all system locations by
              default.
            </p>
          </div>
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
            <label className="block text-xs font-bold text-slate-500 uppercase mb-3 flex items-center gap-2">
              <PenTool className="w-3.5 h-3.5 text-teal-600" /> Digital Signature
            </label>
            {formData.signature ? (
              <div className="bg-white border border-dashed border-slate-200 rounded-lg p-3 flex items-center justify-center mb-3">
                <img
                  src={formData.signature}
                  alt="User signature"
                  className="max-h-24 w-auto object-contain"
                />
              </div>
            ) : (
              <p className="text-[10px] text-slate-400 mb-3 italic">No signature on file.</p>
            )}
            <button
              type="button"
              onClick={() => setIsSignatureModalOpen(true)}
              className="bg-white border border-slate-200 text-slate-700 px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all"
            >
              {formData.signature ? 'Update Signature' : 'Add Signature'}
            </button>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-6 py-2 text-slate-500 font-bold hover:bg-slate-50 rounded-xl transition-all"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              const cleanEmail = String(formData.email || '').trim();
              if (!formData.name || !cleanEmail || !formData.userGroupId) {
                showAlertModal('Please complete the required fields (Name, Email, and Group).');
                return;
              }
              if (!isValidEmail(cleanEmail)) {
                showAlertModal('Please enter a valid email address.');
                return;
              }
              if (formData.name && cleanEmail && formData.userGroupId) {
                const role = deriveRoleFromGroup(formData.userGroupId, groups);
                onSave({
                  ...formData,
                  email: cleanEmail,
                  role,
                  id: formData.id || createId()
                } as User);
              }
            }}
            className="bg-slate-900 text-white px-8 py-2 rounded-xl font-bold hover:bg-slate-800 transition-all shadow-lg active:scale-95"
          >
            {formData.id ? 'Update Member' : 'Create Member'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const StaffTab: React.FC<StaffTabProps> = ({
  userGroups,
  venues,
  currentUserGroup,
  onAddUser,
  onUpdateUser,
  onDeleteUser,
  canEdit
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');

  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [viewingSignature, setViewingSignature] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    let isActive = true;
    setIsLoadingUsers(true);
    setUsersError('');
    api
      .listStaffUsers({ page, pageSize, q: search || undefined })
      .then((data) => {
        if (!isActive) return;
        setUsers(data.items || []);
        setTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setUsers([]);
        setTotalCount(0);
        setUsersError(normalizeErrorMessage(err, 'Failed to load staff users.'));
      })
      .finally(() => {
        if (isActive) setIsLoadingUsers(false);
      });

    return () => {
      isActive = false;
    };
  }, [page, pageSize, search]);

  const refreshUsers = async () => {
    setIsLoadingUsers(true);
    setUsersError('');
    try {
      const data = await api.listStaffUsers({ page, pageSize, q: search || undefined });
      setUsers(data.items || []);
      setTotalCount(Number(data.totalCount || 0));
    } catch (err: unknown) {
      setUsers([]);
      setTotalCount(0);
      setUsersError(normalizeErrorMessage(err, 'Failed to load staff users.'));
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingUser) return;
    setIsDeleting(true);
    try {
      await onDeleteUser(deletingUser.id);
      setDeletingUser(null);
      await refreshUsers();
    } catch (err: unknown) {
      console.error('Delete Tab Error:', err);
      showAlertModal(normalizeErrorMessage(err, 'Failed to delete user.'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="animate-fade-in">
      {viewingSignature && (
        <div
          className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
          onClick={() => setViewingSignature(null)}
        >
          <div
            className="bg-white p-6 rounded-xl shadow-2xl max-w-md w-full relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setViewingSignature(null)}
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-bold text-lg mb-4 text-slate-800">Technician Signature</h3>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex justify-center items-center h-40">
              <img
                src={viewingSignature}
                alt="User Signature"
                className="max-h-full max-w-full object-contain"
              />
            </div>
          </div>
        </div>
      )}

      {editingUser && currentUserGroup && (
        <UserModal
          user={editingUser}
          groups={userGroups}
          venues={venues}
          currentUserGroup={currentUserGroup}
          onSave={async (u) => {
            const signatureChanged =
              typeof u.signature === 'string' && u.signature !== editingUser.signature;
            if (u.id && users.some((existing) => existing.id === u.id)) {
              await Promise.resolve(onUpdateUser(u));
            } else {
              await Promise.resolve(onAddUser(u));
            }
            if (signatureChanged) {
              showAlertModal('Signature saved.');
            }
            setEditingUser(null);
            await refreshUsers();
          }}
          onClose={() => setEditingUser(null)}
        />
      )}

      {/* Deletion Confirmation Modal */}
      {deletingUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-scale-up border border-slate-200">
            <div className="p-8 text-center">
              <div className="bg-red-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Trash2 className="w-10 h-10 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Revoke Staff Access?</h3>
              <p className="text-slate-500 text-xs mb-4 leading-relaxed">
                Are you sure you want to remove <strong>{deletingUser.name}</strong>?<br />
                <span className="text-[10px] font-mono bg-slate-100 px-1 rounded">
                  Record ID: {deletingUser.id}
                </span>
              </p>
              <div className="flex flex-col gap-3">
                <button
                  disabled={isDeleting}
                  onClick={handleDeleteConfirm}
                  className="w-full bg-red-600 text-white py-3 rounded-2xl font-bold hover:bg-red-700 transition-all shadow-lg shadow-red-600/20 flex items-center justify-center gap-2"
                >
                  {isDeleting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  {isDeleting ? 'Deleting...' : 'Yes, Delete Member'}
                </button>
                <button
                  disabled={isDeleting}
                  onClick={() => setDeletingUser(null)}
                  className="w-full bg-slate-100 text-slate-600 py-3 rounded-2xl font-bold hover:bg-slate-200 transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-bold text-slate-800">Staff Management</h3>
          <p className="text-xs text-slate-500 mt-1">
            Configure internal users, roles, and site permissions.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search staff..."
              className="pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none w-56 bg-white text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {canEdit && (
            <button
              onClick={() =>
                setEditingUser({
                  id: '',
                  name: '',
                  email: '',
                  role: 'staff',
                  userGroupId: '',
                  assignedVenueIds: []
                })
              }
              className="bg-slate-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-slate-800 shadow-sm transition-all active:scale-95"
            >
              <Plus className="w-4 h-4" /> Add Staff Member
            </button>
          )}
        </div>
      </div>

      {/* Duplicate Warning */}
      {users.some((u, i) => users.findIndex((o) => o.email === u.email) !== i) && (
        <div className="mb-6 bg-amber-50 border border-amber-100 p-4 rounded-xl flex items-start gap-4">
          <ShieldAlert className="w-6 h-6 text-amber-600 shrink-0" />
          <div>
            <p className="text-sm font-bold text-amber-900">Duplicate Profiles Detected</p>
            <p className="text-xs text-amber-700 mt-1 leading-relaxed">
              Multiple records found with the same email. This usually happens after migrating from
              Demo data to Auth. Please delete the <strong>"Demo"</strong> records (Short IDs) to
              clean up your dashboard.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="p-4 font-bold text-[10px] uppercase tracking-widest text-slate-500">
                Source
              </th>
              <th className="p-4 font-bold text-[10px] uppercase tracking-widest text-slate-500">
                Name & Email
              </th>
              <th className="p-4 font-bold text-[10px] uppercase tracking-widest text-slate-500">
                Access Group
              </th>
              <th className="p-4 font-bold text-[10px] uppercase tracking-widest text-slate-500">
                Authorized Sites
              </th>
              <th className="p-4 font-bold text-[10px] uppercase tracking-widest text-slate-500 text-center">
                Digital Sig
              </th>
              <th className="p-4 font-bold text-[10px] uppercase tracking-widest text-slate-500 text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoadingUsers ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                  Loading staff users...
                </td>
              </tr>
            ) : usersError ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-red-500">
                  {usersError}
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-slate-500 italic">
                  No staff users found.
                </td>
              </tr>
            ) : (
              users.map((user) => {
                const group = userGroups.find((g) => g.id === user.userGroupId);
                const isGlobalAdminUser = group?.type === 'global_admin';
                const isUuid =
                  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(user.id);

                const canEditThisUser =
                  canEdit && (currentUserGroup?.type === 'global_admin' || !isGlobalAdminUser);
                const assignedCount = user.assignedVenueIds?.length || 0;

                return (
                  <tr
                    key={user.id}
                    className="last:border-0 hover:bg-slate-50/50 transition-colors group"
                  >
                    <td className="p-4">
                      {isUuid ? (
                        <div className="flex items-center gap-1.5 text-[9px] font-black uppercase text-teal-600 bg-teal-50 px-2 py-1 rounded-full border border-teal-100 w-fit">
                          <ShieldCheck className="w-3.5 h-3.5" /> Live Auth
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-[9px] font-black uppercase text-amber-600 bg-amber-50 px-2 py-1 rounded-full border border-amber-100 w-fit">
                          <Sparkles className="w-3.5 h-3.5" /> Demo/Seed
                        </div>
                      )}
                      <div className="mt-1.5 text-[9px] font-mono text-slate-300 px-2">
                        ID: {user.id.substring(0, 8)}...
                      </div>
                    </td>
                    <td className="p-4 font-medium text-slate-900">
                      <div className="font-bold flex items-center gap-2">
                        {user.name || (
                          <span className="text-slate-300 italic font-normal text-xs">
                            No Name Set
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                        {user.email}
                      </div>
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-tight border ${isGlobalAdminUser ? 'bg-purple-50 text-purple-700 border-purple-100' : 'bg-slate-100 text-slate-600 border-slate-200'}`}
                      >
                        {group?.name || 'Authorized Staff'}
                      </span>
                    </td>
                    <td className="p-4">
                      {assignedCount === 0 ? (
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                          Full Access
                        </span>
                      ) : (
                        <div className="flex items-center gap-1.5 text-teal-700 font-bold text-[10px] uppercase">
                          <MapPin className="w-3 h-3" /> {assignedCount} Sites
                        </div>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {user.signature ? (
                        <button
                          onClick={() => setViewingSignature(user.signature || null)}
                          className="inline-flex items-center gap-1.5 text-teal-700 hover:text-teal-900 text-xs font-bold bg-teal-50 hover:bg-teal-100 px-3 py-1.5 rounded-lg border border-teal-100 transition-all shadow-sm"
                        >
                          <PenTool className="w-3 h-3" /> View
                        </button>
                      ) : (
                        <span className="text-[10px] text-slate-300 font-bold uppercase opacity-50">
                          Not Set
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {canEditThisUser ? (
                          <>
                            <button
                              onClick={() => setEditingUser(user)}
                              className="text-slate-400 hover:text-teal-600 p-2 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-100"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            {/* Deletion logic is now safe because we target the specific ID row */}
                            <button
                              onClick={() => setDeletingUser(user)}
                              className="text-slate-400 hover:text-red-600 p-2 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-100"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <span className="text-[9px] text-slate-400 font-black uppercase tracking-widest px-2 py-2">
                            Protected
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        isLoading={isLoadingUsers}
      />
    </div>
  );
};
